import React, { FC } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import { CountryPreviewFragment } from "__generated__/graphql";
import { getCountryOptions } from "helpers/getCountryOptions";
import { WithLabel } from "common/fieldLabel/WithLabel";
import { checkOptionEqualToValue } from "./checkOptionEqualToValue";
import { useTranslation } from "react-i18next";

export type ICountryAutoCompleteFieldValue =
  | CountryPreviewFragment[]
  | CountryPreviewFragment
  | null;

interface ICountryAutocompleteFieldProps {
  value: ICountryAutoCompleteFieldValue;
  setValue: (value: ICountryAutoCompleteFieldValue) => void;
  label?: string;
  labelledById?: string;
  errorMessage?: string;
  touched?: boolean;
  showLabel?: boolean;
  multiple?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}
export const CountryAutocompleteField: FC<ICountryAutocompleteFieldProps> = ({
  label,
  value,
  setValue,
  showLabel,
  labelledById,
  errorMessage,
  multiple,
  onKeyDown,
  touched,
}) => {
  const { t: translateUi } = useTranslation("ui");
  const { t: translate, i18n } = useTranslation("common");

  const defaultLabel = translate("fields.labels.country");
  const fieldLabel = label || defaultLabel;
  const labelId = `country_autocomplete_${fieldLabel.replaceAll(" ", "_")}`;
  const countryOptions = getCountryOptions(i18n.language);
  const handleChangeCountry = (
    event,
    newCountry: ICountryAutoCompleteFieldValue,
  ) => {
    setValue(newCountry);
  };
  return (
    <Autocomplete
      isOptionEqualToValue={checkOptionEqualToValue}
      options={countryOptions}
      autoHighlight
      filterSelectedOptions
      loadingText={translateUi("status.loading")}
      getOptionLabel={(option: CountryPreviewFragment) => option.name}
      renderOption={(props, option: CountryPreviewFragment) => (
        <li {...props} data-cy={option.name}>
          {option.emojiFlag} {option.name} ({option.code})
        </li>
      )}
      multiple={multiple}
      value={value}
      onChange={handleChangeCountry}
      renderInput={(params) => (
        <WithLabel showLabel={showLabel} label={fieldLabel} labelId={labelId}>
          <TextField
            {...params}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
            }}
            InputProps={{
              ...params.InputProps,
            }}
            error={touched && errorMessage ? true : false}
            helperText={errorMessage}
            data-cy="country"
            aria-labelledby={showLabel ? labelId : labelledById}
            placeholder={translate("fields.placeholders.select_country")}
            onKeyDown={onKeyDown}
          />
        </WithLabel>
      )}
    />
  );
};
